import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import butterflyApi from '../ButterflyApi';
import {
  CATEGORY_LABELS,
  INQUIRY_STATUSES,
  STATUS_BADGE_CLASS,
  STATUS_LABELS,
  formatInquiryDate,
  getApiErrorMessage,
} from './supportInquiryUtils';

const ManageInquiryDetail = () => {
  const navigate = useNavigate();
  const { inquiryNo } = useParams();
  const [inquiry, setInquiry] = useState()
  const [status, setStatus] = useState('WAITING')
  const [answerContent, setAnswerContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setMessage('')
    setErrorMessage('')
    const response = await butterflyApi.getManageInquiry(inquiryNo, error => {
      setErrorMessage(getApiErrorMessage(error, '문의 내용을 불러오지 못했습니다.'))
    })

    if (response) {
      setInquiry(response)
      setStatus(response.status)
      setAnswerContent(response.answerContent || '')
    }
    setLoading(false)
  }, [inquiryNo])

  const save = async () => {
    if (status === 'ANSWERED' && !answerContent.trim()) {
      setErrorMessage('답변 내용을 입력해주세요.')
      return
    }

    setSaving(true)
    setMessage('')
    setErrorMessage('')
    const response = await butterflyApi.updateManageInquiry(
      inquiryNo,
      status,
      answerContent.trim() ? answerContent.trim() : null,
      error => {
        setErrorMessage(getApiErrorMessage(error, '문의 답변을 저장하지 못했습니다.'))
      },
    )
    setSaving(false)

    if (response) {
      setInquiry(response)
      setStatus(response.status)
      setAnswerContent(response.answerContent || '')
      setMessage('저장되었습니다.')
    }
  }

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return <div className='px-4 py-8 text-sm text-gray-500'>로딩중...</div>
  }

  if (!inquiry) {
    return <div className='px-4 py-8'>
      <div className='mb-4'>
        <a href='/manage/inquiries' className='text-blue-600 underline'>문의 목록</a>
      </div>
      <div className='text-xl font-bold'>문의 내용을 찾을 수 없습니다.</div>
      {errorMessage && <div className='mt-3 text-sm text-red-600'>{errorMessage}</div>}
    </div>
  }

  return <div className='max-w-4xl px-4 py-8'>
    <div className='mb-4 flex gap-3'>
      <a href='/manage' className='text-blue-600 underline'>Manage</a>
      <button type='button' className='text-blue-600 underline' onClick={() => navigate('/manage/inquiries')}>문의 목록</button>
    </div>

    <div className='flex items-start justify-between gap-4'>
      <div>
        <div className='text-sm font-bold text-gray-500'>{CATEGORY_LABELS[inquiry.category] || inquiry.category}</div>
        <h1 className='mt-2 text-2xl font-bold'>{inquiry.title}</h1>
      </div>
      <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_BADGE_CLASS[inquiry.status] || 'bg-gray-100 text-gray-500'}`}>
        {STATUS_LABELS[inquiry.status] || inquiry.status}
      </span>
    </div>

    <div className='mt-6 grid gap-3 rounded border border-gray-200 bg-gray-50 p-4 text-sm md:grid-cols-2'>
      <div>문의 번호: <b>{inquiry.inquiryNo}</b></div>
      <div>사용자 번호: <b>{inquiry.supporterNo}</b></div>
      <div>주인공 번호: <b>{inquiry.heroNo || '-'}</b></div>
      <div>접수 일시: <b>{formatInquiryDate(inquiry.registerDatetime)}</b></div>
      <div>수정 일시: <b>{formatInquiryDate(inquiry.updateDatetime)}</b></div>
      <div>답변 일시: <b>{formatInquiryDate(inquiry.answeredDatetime)}</b></div>
      <div>종료 일시: <b>{formatInquiryDate(inquiry.closedDatetime)}</b></div>
      <div>답변자: <b>{inquiry.answeredBy || '-'}</b></div>
    </div>

    <section className='mt-6 rounded border border-gray-200 p-4'>
      <h2 className='font-bold'>문의 내용</h2>
      <p className='mt-3 whitespace-pre-wrap text-sm leading-7'>{inquiry.content}</p>
    </section>

    <section className='mt-6 rounded border border-gray-200 p-4'>
      <h2 className='font-bold'>답변 및 상태</h2>
      <label className='mt-4 block'>
        <span className='block text-xs font-bold text-gray-600'>상태</span>
        <select className='mt-1 rounded border border-gray-300 px-3 py-2 text-sm' value={status} onChange={event => setStatus(event.target.value)}>
          {INQUIRY_STATUSES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
      </label>
      <label className='mt-4 block'>
        <span className='block text-xs font-bold text-gray-600'>답변 내용</span>
        <textarea
          className='mt-1 min-h-52 w-full rounded border border-gray-300 px-3 py-2 text-sm leading-6'
          value={answerContent}
          onChange={event => setAnswerContent(event.target.value)}
          placeholder='답변 내용을 입력하세요.'
        />
      </label>
      {message && <div className='mt-3 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700'>{message}</div>}
      {errorMessage && <div className='mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600'>{errorMessage}</div>}
      <button
        type='button'
        className='mt-4 rounded bg-blue-600 px-5 py-2 text-sm font-bold text-white disabled:bg-gray-300'
        disabled={saving}
        onClick={save}
      >
        {saving ? '저장 중...' : '저장'}
      </button>
    </section>
  </div>
}

export default ManageInquiryDetail
